import React, { useEffect, useState } from "react";
import { patch } from "../lib/api";
import { formatDay } from "../lib/format";
import { LEAD_STAGES, leadError, leadName, moveNeeds, stageIcon, stageLabel } from "../lib/leads";
import { todayISO } from "../../helpers/datetime";
import { Button, Chip, Field, Input, Modal, Spinner, Textarea } from "../ui";
import NextStepFields from "./NextStepFields";
import { useShell } from "../shell/context";

// Move a lead to another stage. An open stage asks for the next step and its
// date (prefilled; a date already past is cleared); Won/Lost ask for the
// outcome reason. The API's 422 codes show inline on the field.
export default function MoveStageModal({ lead, to, onClose, onMoved }) {
    const { toast, bump } = useShell();
    const today = todayISO();
    const [target, setTarget] = useState(to);
    const [step, setStep] = useState("");
    const [date, setDate] = useState("");
    const [reason, setReason] = useState("");
    const [closedAt, setClosedAt] = useState(today);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!lead) return;
        setTarget(to && to !== lead.stage ? to : null);
        setStep(lead.next_step || "");
        setDate(lead.next_step_date && lead.next_step_date >= today ? lead.next_step_date : "");
        setReason(lead.outcome_reason || "");
        setClosedAt(today);
        setError(null);
        setBusy(false);
    }, [lead, to, today]);

    if (!lead) return null;
    const needs = moveNeeds(lead, target);

    const save = async () => {
        if (!target) return setError({ message: "Pick the stage to move to.", field: null });
        let body;
        if (needs === "next_step") {
            if (!step.trim() || !date) return setError({ message: "An open lead needs a next step and a date for it.", field: "next_step" });
            body = { stage: target, next_step: step.trim(), next_step_date: date };
        } else {
            if (!reason.trim()) return setError({ message: `Say why the lead was ${target}.`, field: "outcome_reason" });
            body = { stage: target, outcome_reason: reason.trim() };
            if (closedAt && closedAt !== today) body.closed_at = closedAt;
        }
        setBusy(true);
        setError(null);
        try {
            const res = await patch(`v1/crm/leads/${lead.id}`, body);
            toast(`${leadName(lead)} moved to ${stageLabel(target)}.`, { icon: stageIcon(target) });
            bump();
            onMoved && onMoved(res);
            onClose();
        } catch (e) {
            setError(leadError(e));
            setBusy(false);
        }
    };

    return (
        <Modal
            open
            title={`Move "${leadName(lead)}"`}
            onClose={onClose}
            width={520}
            footer={
                <>
                    <Button variant="light" onClick={onClose}>Cancel</Button>
                    <Button variant={target === "lost" ? "danger" : "primary"} onClick={save} disabled={busy || !target}>
                        {busy && <Spinner />}
                        {target ? `Move to ${stageLabel(target)}` : "Move"}
                    </Button>
                </>
            }
        >
            <div className="hz-form-grid" onKeyDown={(e) => (e.metaKey || e.ctrlKey) && e.key === "Enter" && save()}>
                <div>
                    <span className="hz-label">From {stageLabel(lead.stage)} to</span>
                    <div className="hz-row-wrap" style={{ gap: 6 }}>
                        {LEAD_STAGES.filter((s) => s !== lead.stage).map((s) => (
                            <Chip key={s} icon={stageIcon(s)} active={target === s} onClick={() => { setTarget(s); setError(null); }}>
                                {stageLabel(s)}
                            </Chip>
                        ))}
                    </div>
                </div>
                {error && <div className="hz-alert hz-alert-danger">{error.message}</div>}
                {needs === "next_step" && (
                    <>
                        {!lead.is_open && <div className="hz-alert hz-alert-info hz-small">This reopens the lead: its close date and outcome reason are cleared.</div>}
                        <NextStepFields
                            step={step}
                            date={date}
                            onStep={(e) => setStep(e.target.value)}
                            onDate={(v) => setDate(v && v.target ? v.target.value : v)}
                            invalid={error && error.field === "next_step"}
                            from={today}
                            autoFocus
                        />
                        {lead.next_step_date && lead.next_step_date < today && (
                            <div className="hz-hint" style={{ marginTop: -8 }}>
                                The last next-step date ({formatDay(lead.next_step_date, { short: true })}) has passed; pick a new one.
                            </div>
                        )}
                    </>
                )}
                {needs === "outcome" && (
                    <>
                        <Field label={target === "won" ? "Why it was won" : "Why it was lost"} required>
                            <Textarea
                                rows={2}
                                autoFocus
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                className={error && error.field === "outcome_reason" && !reason.trim() ? "hz-invalid" : ""}
                                placeholder={target === "won" ? "Board approved the budget" : "Budget moved to another project"}
                            />
                        </Field>
                        <Field label="Closed on" style={{ maxWidth: 200 }}>
                            <Input type="date" value={closedAt} min={lead.opened_at || undefined} max={today} onChange={(e) => setClosedAt(e.target.value)} />
                        </Field>
                    </>
                )}
            </div>
        </Modal>
    );
}
